import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontFredoka, theme } from "@product/scmp-sdk";
import "swiper/css";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";

import { touchOptimizedStyles } from "shared/components/common/base-link/styles";

import ArrowPrevious from "./arrow-previous.svg";
import Sand1024 from "./sand-1024.svg?url";
import Sand1440 from "./sand-1440.svg?url";
import Sand375 from "./sand-375.svg?url";
import Sand768 from "./sand-768.svg?url";

export const Container = styled.div`
  position: relative;

  inline-size: 100%;
  ${theme.breakpoints.up("desktop")} {
    padding-block-end: 3px;
  }
  ${theme.breakpoints.up("largeDesktop")} {
    padding-block-end: 52px;
  }
`;

export const SwiperContainer = styled.div`
  position: relative;

  inline-size: 100%;
  padding-inline: 16px;
  box-sizing: border-box;
  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 692px;
    margin-inline: auto;
    padding-block: 0;
    padding-inline: 0;
  }
  ${theme.breakpoints.up("desktop")} {
    max-inline-size: 768px;
    margin-block: 0;
    margin-inline: auto;
  }
`;
export const StyledSwiper = styled(Swiper)`
  overflow-y: hidden;
  ${theme.breakpoints.up("desktop")} {
    block-size: 542px;
  }

  & {
    .swiper-wrapper {
      block-size: 100%;
    }
    .swiper-pagination.swiper-pagination-horizontal {
      /* stylelint-disable-next-line */
      top: unset;
      bottom: 0;
      .swiper-pagination-bullet {
        inline-size: 6px;
        block-size: 6px;
        margin-block: 0;
        margin-inline: 4px;

        background-color: #0f75bf;

        opacity: 0.3;
        ${theme.breakpoints.up("tablet")} {
          inline-size: 10px;
          block-size: 10px;
          margin-inline: 7px;
        }

        &.swiper-pagination-bullet-active {
          opacity: 1;
        }
      }
    }
  }
`;

export const StyledSwiperSlide = styled(SwiperSlide)`
  block-size: 100%;
  padding-block-end: 17px;
  ${theme.breakpoints.up("tablet")} {
    padding-block-end: 55px;
  }
  ${theme.breakpoints.up("desktop")} {
    padding-block-end: 20px;
  }
`;

type HeroProps = {
  $url?: string;
};
export const Hero = styled.a<HeroProps>`
  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 8.17px;

  background-size: cover;

  aspect-ratio: 343/228;
  ${theme.breakpoints.up("desktop")} {
    border-radius: 12.61px;
  }
  ${theme.breakpoints.up("desktop")} {
    inline-size: 768px;
    margin-block: 0;

    border-radius: 14px;
  }

  ${props => css`
    background-image: url(${props.$url});
  `}
`;

export const SubHeadline = styled.div`
  margin-block-start: 26px;

  font-weight: 400;
  font-size: 22px;
  line-height: 140%;
`;

export const Offer = styled.div`
  margin-block-start: 28px;

  font-weight: 600;
  font-size: 32px;
  line-height: 100%;
`;

export const ContentButton = styled.button`
  padding-block: 12px;
  padding-inline: 24px;

  font-weight: 600;
  font-size: 16px;
  font-family: ${fontFredoka};
  line-height: 20px;
  letter-spacing: 0.03em;

  border: 2px solid #000000;
  border-radius: 100px;

  background-color: #fecc17;

  ${touchOptimizedStyles};
`;

const ArrowStyle = css`
  position: absolute;
  inset-block-start: 50%;
  z-index: 1;

  cursor: pointer;
`;

export const StyledArrowPrevious = styled(ArrowPrevious)`
  ${ArrowStyle}
  inset-inline-start: 0;

  transform: translateY(-50%);
`;
export const StyledArrowNext = styled(ArrowPrevious)`
  ${ArrowStyle}
  inset-inline-end: 0;

  transform: rotate(180deg) translateY(50%);
`;

type SwiperButtonProps = {
  $isNext?: boolean;
};
export const SwiperButton = styled.div<SwiperButtonProps>`
  position: absolute;
  inset-block-start: 0;

  block-size: 100%;

  svg {
    inline-size: 24px;
    block-size: 24px;
  }
  ${theme.breakpoints.up("tablet")} {
    svg {
      inline-size: 48px;
      block-size: 48px;
    }
  }
  ${props =>
    props.$isNext
      ? `
      right: 26px;
      ${theme.breakpoints.up("tablet")} {
        right: 10px;
      }

      ${theme.breakpoints.up("desktop")} {
        right: 0;
        svg {
          position: absolute;
          left: 16px;
        }
      }}
    `
      : `
      left: 26px;
      ${theme.breakpoints.up("tablet")} {
        left: 10px;
      }

      ${theme.breakpoints.up("desktop")} {
        left: 0;
        svg {
          position: absolute;
          left: -64px;
        }
      }}
    `};

  ${touchOptimizedStyles};
`;

export const Sand = styled.div`
  position: relative;
  inset-block-end: 0;
  inset-inline-start: 0;

  inline-size: 100%;
  aspect-ratio: 375 / 39;

  background-image: url(${Sand375});
  background-repeat: no-repeat;
  background-size: cover;

  ${theme.breakpoints.up("tablet")} {
    background-image: url(${Sand768});
    aspect-ratio: 768 / 39;
  }

  ${theme.breakpoints.up("desktop")} {
    position: absolute;

    aspect-ratio: 1024 / 106;

    background-image: url(${Sand1024});
  }

  ${theme.breakpoints.up("largeDesktop")} {
    aspect-ratio: 1440 / 156;

    background-image: url(${Sand1440});
  }
`;
